
import { Navigation } from "@/components/Navigation";
import { Sidebar } from "@/components/Sidebar";
import { ShopCarousel } from "@/components/ShopCarousel";
import { ShopTabs } from "@/components/ShopTabs";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shirt, Search, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { Hero } from "@/components/blocks/hero";
import { motion } from "framer-motion";
import { SparklesText } from "@/components/ui/sparkles-text";
import { useRef, useEffect } from "react";
import { StackedCardsInteraction } from "@/components/ui/stacked-cards-interaction";
import { ImageWebp } from "@/components/ui/image-webp";
import HeroBadge from "@/components/ui/hero-badge";

const Shop = () => {
  // References for scroll targets
  const produtosRef = useRef<HTMLDivElement>(null);
  const colecoesRef = useRef<HTMLDivElement>(null);

  // Function to handle smooth scrolling to sections
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Shop cards data using available images
  const shopCards = [
    {
      image: "/lovable-uploads/4950ff22-9907-430d-82b7-dcf8964491a1.png",
      title: "Coleções Especiais",
      description: "Descubra as últimas tendências em moda inspirada em anime"
    },
    {
      image: "/lovable-uploads/a27999f7-bc34-4b87-b0df-488c92ddbf7a.png",
      title: "Livraria",
      description: "Conheça nossa nova coleção de livros e mangás exclusivos"
    },
    {
      image: "/lovable-uploads/2b3aeb56-eb2e-4acf-a580-94aa007a407a.png",
      title: "Colecionáveis",
      description: "Itens especiais para colecionadores e fãs de anime"
    }
  ];

  // Preload images to improve performance
  useEffect(() => {
    shopCards.forEach(card => {
      const img = new Image();
      img.src = card.image;
    });
  }, []);

  return (
    <div className="min-h-screen">
      {/* Header with logo and navigation */}
      <header className="py-4 px-6 bg-background border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-primary p-1.5 rounded text-primary-foreground">
            <Shirt className="h-5 w-5" />
          </div>
          <h1 className="font-semibold text-lg">Ink Shop</h1>
        </div>
        <div className="max-w-md w-full hidden md:block mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar produtos..." className="pl-10" />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <CartDrawer />
          <Link to="/checkout">
            <Button variant="outline" className="hidden sm:flex">
              <ShoppingBag className="mr-2 h-4 w-4" />
              Finalizar Compra
            </Button>
          </Link>
        </div>
      </header>

      <div className="hidden sm:block">
        <Sidebar />
      </div>
      
    <main className="px-4 sm:pl-16 sm:pr-16 pt-8 pb-20 sm:pb-16 transition-all duration-300">
      {/* Hero Banner with promotional text and stacked cards */}
      <div className="max-w-7xl mx-auto mb-12">
        <Hero
          pill={{
            text: "Coleção Exclusiva",
            icon: <Shirt className="h-4 w-4" />,
            variant: "default",
            size: "md",
          }}
          content={{
            title: "A loja oficial de",
            titleHighlight: "criadores e séries da INK WEBTOONS",
            description: "Apoie seus criadores favoritos comprando roupas e acessórios exclusivos da série! Confira todos os produtos incríveis que nossos criadores têm a oferecer.",
            primaryAction: {
              text: "Ver Produtos",
              href: "#produtos",  // Change onClick to href
            },
            secondaryAction: {
              text: "Coleções Especiais",
              href: "#colecoes",  // Change onClick to href
            },
          }}
          preview={
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="h-[420px] flex items-center justify-center"
            >
              <StackedCardsInteraction 
                cards={shopCards} 
                spreadDistance={60}
                rotationAngle={8}
              />
            </motion.div>
          }
          previewClassName="lg:max-w-2xl"
        />
      </div>

      {/* Category filters */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex overflow-x-auto pb-4 gap-2 no-scrollbar mx-[15px] px-[14px] my-0">
          <Button variant="secondary" className="rounded-full">
            Todos
          </Button>
          <Button variant="outline" className="rounded-full">
            Roupas unissex
          </Button>
          <Button variant="outline" className="rounded-full">
            Acessórios
          </Button>
          <Button variant="outline" className="rounded-full">
            História em quadrinhos
          </Button>
          <Button variant="outline" className="rounded-full">
            Romance
          </Button>
          <Button variant="outline" className="rounded-full">
            Edição limitada
          </Button>
        </div>
      </div>

      {/* Carousel for promotions with ID for navigation */}
      <div id="colecoes" ref={colecoesRef} className="scroll-mt-20">
        <ShopCarousel />
      </div>
      
      {/* Products tabs and grid with ID for navigation */}
      <div id="produtos" ref={produtosRef} className="scroll-mt-20">
        <ShopTabs />
      </div>
    </main>
  </div>
  );
};

export default Shop;
